import type { FC } from 'react';
import { FormGroup } from '@patternfly/react-core/dist/dynamic/components/Form';
import { FormHelperText } from '@patternfly/react-core/dist/dynamic/components/Form';
import { FormSelect } from '@patternfly/react-core/dist/dynamic/components/FormSelect';
import { FormSelectOption } from '@patternfly/react-core/dist/dynamic/components/FormSelect';
import { HelperText } from '@patternfly/react-core/dist/dynamic/components/HelperText';
import { HelperTextItem } from '@patternfly/react-core/dist/dynamic/components/HelperText';
import type { FormikValues } from 'formik';
import { useField, useFormikContext } from 'formik';
import { useFormikValidationFix } from '../../hooks/useFormikValidationFix';
import type { FormSelectFieldOption, FormSelectFieldProps } from './field-types';
import { getFieldId } from './field-utils';

const FormSelectField: FC<FormSelectFieldProps> = ({
  label,
  helpText,
  required,
  options,
  ...props
}) => {
  const [field, { touched, error }] = useField(props.name);
  const { setFieldValue, setFieldTouched } = useFormikContext<FormikValues>();
  const fieldId = getFieldId(props.name, 'dropdown');
  const isValid = !(touched && error);
  const errorMessage = !isValid ? error : '';

  useFormikValidationFix(field.value);

  // PF bug workaround
  // Return to field.value when fixed: https://github.com/patternfly/patternfly-react/issues/5687
  const hasSelectedOption: boolean = options.some(({ value }) => field.value === value);
  const placeholderOption: FormSelectFieldOption = options.find(
    ({ isPlaceholder }) => isPlaceholder,
  );
  const safeValue: string = hasSelectedOption
    ? field.value
    : placeholderOption
    ? placeholderOption.value
    : field.value;

  return (
    <FormGroup fieldId={fieldId} label={label} isRequired={required}>
      <FormSelect
        {...props}
        id={fieldId}
        aria-describedby={helpText ? `${fieldId}-helper` : undefined}
        onChange={(_event, value: any) => {
          props.onChange && props.onChange(value);
          // Validation is automatically done by the useFormikValidationFix above
          setFieldValue(props.name, value, false);
          setFieldTouched(props.name, true, false);
        }}
        value={safeValue}
      >
        {options.map((option) => (
          <FormSelectOption {...option} key={option.label} />
        ))}
      </FormSelect>

      <FormHelperText>
        <HelperText>
          {!isValid ? (
            <HelperTextItem variant="error">{errorMessage}</HelperTextItem>
          ) : (
            <HelperTextItem>{helpText}</HelperTextItem>
          )}
        </HelperText>
      </FormHelperText>
    </FormGroup>
  );
};

export default FormSelectField;
